import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {ChatMessage} from "../../models/chat-message";
import {AuthService} from '../auth/auth.service';
import {ChatMessageProcessor} from './chat-message-processor.service';
import {ChatCommunicationService} from './chat-communication.service';
import {TelemetryService} from '../telemetry/telemetry.service';
import {LoggingService} from '../logging/logging.service';
import {ConnectionOrchestrator, ConnectionState} from '../connection/connection-manager.service';
import {ConversationManager} from '../conversation/conversation-manager.service';
import {AppLifecycleService} from '../lifecycle/app-lifecycle.service';
import {MessageType, DEFAULT_MESSAGE_TYPE} from '../../models/message-type';
import {trace, context} from '@opentelemetry/api';
import {TracingContext} from '../../models/tracing-context';
import {MessageStoreService} from '../persistence/message-store.service';

@Injectable({
  providedIn: 'root',
})
export class ChatService implements OnDestroy {
  private messages: ChatMessage[] = [];
  private messagesSubject = new BehaviorSubject<ChatMessage[]>(this.messages);
  public messages$ = this.messagesSubject.asObservable();
  private destroy$ = new Subject<void>();
  private isInitializing = false;
  private messageTimeouts = new Map<string, any>(); // Track timeouts for messages

  // 🔥 PHASE 3: Old IndexedDB properties removed - MessageStore handles all persistence
  // IndexedDB restoration completion signal (CRITICAL for chat-main to wait)
  private restorationCompleteSubject = new BehaviorSubject<boolean>(false);
  public restorationComplete$ = this.restorationCompleteSubject.asObservable();

  constructor(
    private authService: AuthService,
    private chatMessageProcessor: ChatMessageProcessor,
    private chatCommunicationService: ChatCommunicationService,
    private telemetryService: TelemetryService,
    private logger: LoggingService,
    private connectionManager: ConnectionOrchestrator,
    private conversationManager: ConversationManager,
    private lifecycle: AppLifecycleService, // Native-app-like lifecycle management
    private messageStore: MessageStoreService // Single source of truth for messages
  ) {
    // Initialize communication channels immediately
    this.chatCommunicationService.initialize();

    // 🔥 CRITICAL FIX: Set up MessageStore subscription FIRST, before any early returns
    // This subscription must be active for clearMessages() to work correctly
    // BUG: Previously this was after the localStorage fast-path, so after page refresh
    // when fast-path returned early, the subscription was never established!
    console.log('[CHAT-SERVICE] 🔬 Setting up MessageStore subscription (CONSTRUCTOR - TOP)');
    this.messageStore.messages$
      .pipe(takeUntil(this.destroy$))
      .subscribe(messages => {
        console.log('[CHAT-SERVICE] 🔬 Received messages from MessageStore', {
          messageCount: messages.length,
          timestamp: Date.now()
        });
        this.messages = messages;
        this.messagesSubject.next(messages);

        // ✅ REMOVED: processor.updateMessages() - processor now subscribes to MessageStore directly
        // No manual synchronization needed - reactive updates happen automatically

        // 🔥 PERFORMANCE: Update localStorage cache for instant restoration after page reload
        // This survives browser tab memory cleanup and provides synchronous access
        if (messages.length > 0) {
          const conversationId = this.conversationManager.getActiveConversationId();
          const cacheData = {
            conversationId,
            timestamp: Date.now(),
            messages: messages.map(m => ({
              messageId: m.messageId,
              conversationId: m.conversationId,
              content: m.content.getValue(),
              bot: m.bot,
              timestamp: m.timestamp,
              pictureUrl: m.pictureUrl,
              operationType: m.operationType,
              messageType: m.messageType
            }))
          };
          try {
            localStorage.setItem('fw_message_cache', JSON.stringify(cacheData));
          } catch (error) {
            // localStorage might be full - clear old cache and try again
            this.logger.warn('ChatService', 'Failed to update localStorage cache', error);
            try {
              localStorage.removeItem('fw_message_cache');
              localStorage.setItem('fw_message_cache', JSON.stringify(cacheData));
            } catch (retryError) {
              this.logger.error('ChatService', 'Failed to update localStorage cache after retry', retryError);
            }
          }
        }
      });

    // 🔥 PERFORMANCE FIX: Use localStorage as synchronous cache for instant restoration
    // When tab is backgrounded for hours and page is reloaded:
    // 1. Angular services are destroyed (BehaviorSubject cache lost)
    // 2. IndexedDB.init() takes 2-4 seconds due to browser throttling
    // 3. localStorage is SYNCHRONOUS and survives page reload - perfect for instant restore!
    const cachedConversationData = localStorage.getItem('fw_message_cache');
    if (cachedConversationData) {
      try {
        const cache = JSON.parse(cachedConversationData);
        const cacheAge = Date.now() - (cache.timestamp || 0);

        // Cache valid for 24 hours
        if (cache.messages && cache.messages.length > 0 && cacheAge < 24 * 60 * 60 * 1000) {
          this.logger.info('ChatService', `🚀 Fast-path restoration - found ${cache.messages.length} cached messages in localStorage (${Math.round(cacheAge / 1000)}s old)`);

          // Restore messages synchronously from cache
          const restoredMessages = cache.messages.map((msg: any) => ({
            ...msg,
            content: new BehaviorSubject<string>(msg.content || ''),
            pictureUrl: msg.bot ? 'assets/svg/bot_icon.svg' : this.authService.getUserProfilePictureUrl()
          }));

          // 🔥 FIX: Update MessageStore's internal state with fast-path messages
          // This ensures that when loadMessages() is called later, it can find these
          // messages by messageId and update them IN PLACE (preserving BehaviorSubject references)
          // WITHOUT THIS: MessageStore.loadMessages() creates NEW instances, breaking subscriptions
          this.messageStore['messagesSubject'].next(restoredMessages);

          // Update BehaviorSubject immediately - UI can render now!
          this.messages = restoredMessages;
          this.messagesSubject.next(restoredMessages);
          // ✅ REMOVED: processor.updateMessages() - processor now subscribes to MessageStore directly

          // Signal restoration complete immediately
          this.restorationCompleteSubject.next(true);

          // Still initialize IndexedDB in background (for persistence and to verify cache)
          this.messageStore.init().then(async () => {
            this.logger.info('ChatService', '✅ MessageStore initialized (background) - verifying cache');

            // Load from IndexedDB and verify cache was correct
            const conversationId = cache.conversationId;
            if (conversationId) {
              // 🔥 FIX (FIN-0065): Use ConversationManager as single source of truth
              this.conversationManager.setActiveConversationId(conversationId);

              // 🔥 FIX (FIN-0069): Reload messages from IndexedDB to get latest content
              // The localStorage cache might be stale (e.g., bot messages were streaming when cached)
              // IndexedDB has the source of truth with full message content
              await this.messageStore.loadMessages(conversationId);
              this.logger.info('ChatService', '✅ Messages reloaded from IndexedDB (overwrites stale cache)');
            }
          });

          return; // Fast-path complete!
        } else {
          this.logger.info('ChatService', `⚠️ localStorage cache expired or empty (age: ${Math.round(cacheAge / 1000)}s)`);
        }
      } catch (error) {
        this.logger.warn('ChatService', 'Failed to parse localStorage cache', error);
      }
    }

    // No valid cache - must wait for IndexedDB (slow path)
    this.logger.info('ChatService', '🐌 Slow-path restoration - no valid cache, waiting for IndexedDB');
    this.messageStore.init().then(async () => {
      this.logger.info('ChatService', '✅ MessageStore initialized');

      // 🔥 CRITICAL: Clear old conversation ID if no messages exist
      // MessageStore v2 upgrade wiped v1 data, but conversation ID persists in localStorage
      // This causes backend to treat fresh starts as reconnections
      const hasMessages = await this.messageStore.hasMessages();
      if (!hasMessages) {
        this.logger.info('ChatService', '🔥 No messages found - clearing old conversation ID to force fresh start');
        this.conversationManager.clearAllConversations();
      }

      // Signal restoration complete so chat-main can proceed
      this.restorationCompleteSubject.next(true);
    });

    // ✅ FIXED: MessageStore subscription moved to TOP of constructor (before early returns)
    // This ensures subscription is always active, even when fast-path localStorage restoration is used

    // 🔥 FIX (FIN-0046): Removed circular dependency that caused infinite loop on page refresh
    // PROBLEM: This subscription created an infinite loop:
    //   1. MessageStore.upsertMessages() → messagesSubject.next()
    //   2. Processor subscribes to messageStore.messages$ → processedMessagesSubject.next()
    //   3. ChatService subscribes to processedMessages$ → calls messageStore.upsertMessages() [THIS LINE]
    //   4. Loop back to step 1 → Result: 68,801 console logs, page hangs for 60+ seconds
    //
    // ROOT CAUSE: Processor ALREADY writes to messageStore when processing chunks (line 507 in processor)
    // This subscription was redundant and caused the loop.
    //
    // EVIDENCE: Test #10 failure showed 68,801 console logs of [MESSAGE-STORE] → [PROCESSOR] → [CHAT-SERVICE]
    // reproducing infinitely until page.goto() timed out after 60 seconds.
    //
    // SOLUTION: Remove this subscription entirely. Processor handles persistence directly.
    // messageStore.upsertMessage() auto-saves to IndexedDB, so no additional persistence is needed.

    // 🔥 PHASE 3 Step 3: Removed redundant IndexedDB subscriptions
    // - Debounced save trigger (MessageStore auto-saves on every update)
    // - Visibility listener (MessageStore handles persistence)
    // - Connection status save (MessageStore handles persistence)
  }

  /**
   * Cleanup resources when service is destroyed
   */
  ngOnDestroy(): void {
    this.logger.info('ChatService', 'Destroying service');
    this.destroy$.next();
    this.destroy$.complete();

    // Clear all pending timeouts
    this.messageTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.messageTimeouts.clear();

    // Make sure to clean up the messages subject
    this.messagesSubject.complete();

    // 🔥 PHASE 3 Step 3: Removed IndexedDB cleanup - MessageStore handles connection lifecycle
  }

  /**
   * Add a user message and send it to the server
   */
  addUserMessage(messageContent: string): void {
    if (!messageContent.trim()) return;

    const addMessageTime = Date.now();
    // Get the current active context (which should include the click span if one exists)
    const currentContext = context.active();
    const parentSpan = trace.getSpan(currentContext);
    
    // Start a new span for this chat interaction, using the current context as parent
    const tracer = trace.getTracer(this.telemetryService.getServiceName());
    const span = tracer.startSpan('chat_user_message', undefined, currentContext);
    const ctx = trace.setSpan(currentContext, span);

    // Execute within the span context
    context.with(ctx, () => {
      const msgTimerId = `user-msg-${addMessageTime}`;
      this.logger.startTimer(msgTimerId);
      this.logger.info('[FRONTEND-CHAT-SERVICE] Adding user message', { 
        length: messageContent.length,
        timestamp: addMessageTime,
        messagesCount: this.messages.length
      });

      // Generate unique messageId for user message
      // Backend will generate its own messageId for the bot response
      const userMessageId = `${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;

      // 🔥 FIX: Use existing active conversation if available (prevents race condition in concurrent sends)
      // Only create a new conversation if there isn't one active already
      let conversationId = this.conversationManager.getActiveConversationId();
      if (!conversationId) {
        conversationId = this.chatMessageProcessor.startNewConversation(userMessageId);
        // 🔥 FIX (FIN-0065): Use ConversationManager as single source of truth
        this.conversationManager.setActiveConversationId(conversationId);
      } else {
        // 🔥 FIX (FIN-0079): Ensure conversation exists and is active before sending message
        // This handles the case where conversation was lost (page reload) or marked inactive
        const userEmail = this.chatCommunicationService.getCurrentUserIdentifier();
        const context = this.conversationManager.getConversation(conversationId);

        if (!context) {
          // Conversation lost (page reload) - recreate it
          this.logger.info('[FRONTEND-CHAT-SERVICE] Conversation lost, recreating', { conversationId });
          this.conversationManager.createConversation(conversationId, userEmail);
        } else if (!context.isActive) {
          // Conversation exists but inactive - reactivate it
          this.logger.info('[FRONTEND-CHAT-SERVICE] Conversation inactive, reactivating', { conversationId });
          this.conversationManager.reactivateConversation(conversationId);
        }
      }

      // Create tracing context from the current span
      const spanContext = span.spanContext();
      const tracingContext: TracingContext = {
        traceId: spanContext.traceId,
        spanId: spanContext.spanId,
        traceFlags: spanContext.traceFlags,
        isRemote: false
      };

      // Create and add the user message with tracing context and conversation context
      const message = this.createMessage(messageContent, false, undefined, userMessageId, tracingContext, conversationId, MessageType.USER);


      this.addMessage(message);

      // Track user message
      this.telemetryService.trackEvent('chat_user_message_sent', {
        length: messageContent.length,
        traceId: message.tracingContext?.traceId,
        spanId: message.tracingContext?.spanId
      }, message.tracingContext?.traceId, message.tracingContext?.spanId);

      // 🔥 FIX: Don't create empty bot placeholder - let backend chunks create it
      // This prevents phantom "Thinking..." messages from appearing when:
      // 1. A previous bot message is still loading
      // 2. User sends a new message
      // 3. Chunks arrive and try to "reuse" the wrong empty message
      // By letting the backend create the bot message, we ensure proper 1-to-1 matching

      this.logger.info('[FRONTEND-CHAT-SERVICE] User message added, waiting for backend response', {
        messagesCount: this.messages.length,
        elapsed: Date.now() - addMessageTime + 'ms'
      });

      // FIN-0100: ALWAYS send immediately - WebSocketMessagingService handles queueing
      // Previously we waited for connection, causing messages to be lost on page refresh
      // during reconnection attempts. Now messages are queued to IndexedDB immediately.
      this.logger.info('[FRONTEND-CHAT-SERVICE] Sending message (will queue if disconnected)', {
        messageId: userMessageId,
        elapsed: Date.now() - addMessageTime + 'ms',
        isConnected: this.connectionManager.isWebSocketAvailable()
      });

      // 🔥 Send only the user message (backend will create bot response)
      const newMessages = [message];
      this.sendMessageToCommunicationService(newMessages);

      // If not connected, also trigger connection establishment in background
      if (!this.connectionManager.isWebSocketAvailable()) {
        this.logger.info('ChatService', 'WebSocket not available, establishing connection in background');
        this.connectionManager.establishConnection();
      }

      this.logger.endTimer(msgTimerId, 'ChatService', 'Message processing and sending');
    });

    // End the span
    span.end();
  }

  /**
   * Send a message via the communication service.
   *
   * ⚠️  DESIGN BY CONTRACT - DO NOT ADD CONNECTION CHECKS HERE!
   *     WebSocketMessagingService.send() handles offline queueing.
   *     Adding checks here BREAKS offline support (FIN-0100).
   */
  private sendMessageToCommunicationService(messages: ChatMessage[]): void {
    const sendTime = Date.now();
    const messageId = messages[messages.length - 1]?.messageId || 'unknown';
    const lastMessage = messages[messages.length - 1];

    // Create tracing span for message sending
    const tracer = this.telemetryService.getTracer();
    if (!tracer) {
      console.warn('Tracer not available, manual span will not be created');
      this.chatCommunicationService.sendMessage(messages);
      return;
    }
    const sendSpan = tracer.startSpan('chat_message_send');

    // Add trace context if available from the message
    if (lastMessage?.tracingContext) {
      sendSpan.setAttribute('trace.parent_trace_id', lastMessage.tracingContext.traceId);
      sendSpan.setAttribute('trace.parent_span_id', lastMessage.tracingContext.spanId);
    }

    sendSpan.setAttributes({
      'message.id': messageId,
      'message.count': messages.length,
      'message.content_length': lastMessage?.content?.value?.length || 0,
      'message.is_user': !lastMessage?.bot,
      'websocket.operation': 'send_message',
      'frontend.component': 'chat-service'
    });

    this.logger.info('[FRONTEND-CHAT-SERVICE] Sending to communication service', {
      messageId,
      messageCount: messages.length,
      sendTime,
      traceId: sendSpan.spanContext().traceId,
      spanId: sendSpan.spanContext().spanId
    });
    
    try {
      this.chatCommunicationService.sendMessage(messages);
      sendSpan.setStatus({code: 1}); // OK
      sendSpan.addEvent('message_sent_to_websocket');

      this.logger.info('[FRONTEND-CHAT-SERVICE] Message sent to communication service', {
        messageId,
        elapsed: Date.now() - sendTime + 'ms',
        traceId: sendSpan.spanContext().traceId
      });
    } catch (error) {
      sendSpan.setStatus({code: 2, message: error instanceof Error ? error.message : 'Unknown error'}); // ERROR
      sendSpan.recordException(error as Error);

      this.logger.error('[FRONTEND-CHAT-SERVICE] Failed to send message', error);
      // Add a bot message indicating the error
      // Get current conversationId from ConversationManager (convert null to undefined)
      const currentConversationId = this.conversationManager.getActiveConversationId() ?? undefined;
      const errorMsg = this.createMessage('Unable to send message. Please check your connection and try again.', true, 'ERROR', undefined, undefined, currentConversationId);
      this.addMessage(errorMsg);
    } finally {
      sendSpan.end();
    }
  }

  /**
   * Create a new chat message
   */
  private createMessage(messageContent: string, isBot: boolean, operationType?: string, messageId?: string, tracingContext?: TracingContext, conversationId?: string, messageType?: MessageType): ChatMessage {
    const pictureUrl = isBot ? 'assets/svg/bot_icon.svg' : this.authService.getUserProfilePictureUrl();
    const id = messageId || `${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;

    const message: ChatMessage = {
      bot: isBot,
      content: new BehaviorSubject<string>(messageContent),
      pictureUrl: pictureUrl,
      timestamp: this.createTimestamp(),
      messageId: id,
      tracingContext: tracingContext,
      conversationId: conversationId,
      messageType: messageType || (isBot ? MessageType.CONVERSATION : MessageType.USER)
    };

    // Add operation type if provided (for progress tracking)
    if (operationType) {
      message.operationType = operationType;

      // Set generation start time for tracking response time
      message.generationStartTime = Date.now();
    }

    return message;
  }

  /**
   * Create a timestamp for a message
   */
  private createTimestamp(): string {
    const now = new Date();
    const isoString = now.toISOString();
    const nanoseconds = now.getMilliseconds().toString().padEnd(6, '0');
    return isoString.replace('Z', nanoseconds + 'Z');
  }

  /**
   * 🔥 FIX: Parse timestamp robustly to handle mixed formats.
   *
   * Problem: Backend returns numeric timestamps (1764030378872) while frontend
   * creates ISO strings ("2025-11-25T00:07:34.000000000Z"). When sorting:
   * - new Date("2025-11-25T00:07:34.000000000Z").getTime() → valid number
   * - new Date(1764030378872).getTime() → valid number (if passed as number)
   * - new Date("1764030378872").getTime() → NaN (string numeric fails!)
   *
   * @param timestamp - Can be: ISO string, number, or numeric string
   * @returns Epoch milliseconds, or 0 if parsing fails
   */
  private parseTimestamp(timestamp: any): number {
    if (!timestamp) return 0;

    // If it's already a number, return it directly
    if (typeof timestamp === 'number') {
      return timestamp;
    }

    // If it's a string, try to parse it
    if (typeof timestamp === 'string') {
      // First try parsing as ISO date string
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return date.getTime();
      }

      // If ISO parsing failed, try parsing as numeric string
      const numericValue = parseInt(timestamp, 10);
      if (!isNaN(numericValue)) {
        return numericValue;
      }
    }

    return 0;
  }

  /**
   * Add a message to the messages list
   * 🔥 PHASE 3 Step 2: Now writes to MessageStore instead of local array
   * 🔥 FIX (FIN-0068): Add skipDuplicateCheck parameter for backend sync
   */
  private addMessage(message: ChatMessage, skipDuplicateCheck = false): void {
    // Prevent duplicate empty bot messages ONLY for the same operation type
    // ❌ SKIP this check when loading from backend (backend is source of truth)
    if(!skipDuplicateCheck && message.bot && (!message.content.getValue() || message.content.getValue().trim() === '')) {
      // Check for duplicates with the same operation type (not just any empty bot message)
      const hasDuplicateOperation = this.messages.some(m =>
        m.bot &&
        (!m.content.getValue() || m.content.getValue().trim() === '') &&
        m.operationType === message.operationType &&
        m.messageId !== message.messageId  // Don't compare against the same message
      );

      if (hasDuplicateOperation) {
        return;
      }
    }

    // Write to MessageStore (single source of truth)
    // MessageStore subscription will update this.messages and emit to UI
    this.messageStore.upsertMessage(message);

    // ✅ PHASE 3: Processor update is now handled by MessageStore.messages$ subscription
    // No need to update here - subscription will sync processor with MessageStore
  }

  /**
   * Get the messages observable
   */
  getMessages(): Observable<ChatMessage[]> {
    return this.messages$;
  }

  /**
   * 🔥 REMOVED (FIN-0068): hasMessages() method deleted - was a false abstraction
   *
   * This method checked in-memory messages (this.messages.length > 0) instead of IndexedDB,
   * causing Tab 2 to incorrectly detect "no messages" due to separate Angular service instances.
   *
   * PROPER ALTERNATIVE: Use MessageStore.hasMessages(conversationId) which queries IndexedDB.
   *
   * The dual implementation (restore vs create) has been eliminated - conversation loading
   * now uses a single code path based on conversationId existence only.
   */

  /**
   * Clear all messages - keep this method for API compatibility
   * 🔥 PHASE 3 Step 2: Now clears from MessageStore instead of local array
   * 🔬 SCIENTIFIC METHOD: Made async to properly await MessageStore clearing (race condition fix)
   */
  async clearMessages(): Promise<void> {
    console.log('[CHAT-SERVICE] 🔬 clearMessages() called - awaiting MessageStore clear');

    // Clear from MessageStore (single source of truth)
    // MessageStore subscription will update this.messages and messagesSubject
    // 🔥 CRITICAL: Must await this! clearMessages() is async (IndexedDB operation)
    await this.messageStore.clearMessages();

    console.log('[CHAT-SERVICE] 🔬 MessageStore cleared - proceeding with other cleanup');

    // Clear processor state
    this.chatMessageProcessor.clearMessages();

    // Clear conversation state through ConversationManager (centralized)
    this.conversationManager.clearAllConversations();

    // 🔥 PHASE 3 Step 3: Removed clearIndexedDBConversation() - MessageStore handles persistence

    this.logger.info('ChatService', 'All chat messages and conversation state cleared via MessageStore');
  }

  /**
   * Clear messages and start a new conversation in a single batched operation
   * This prevents the "flash of empty state" by combining clear + initialize
   * Used for smooth conversation switching (2025 best practice)
   * 🔥 PHASE 3 Step 2: Now uses MessageStore for state management
   */
  clearAndStartNewConversation(conversationId: string): void {
    this.logger.info('ChatService', 'Clearing and starting new conversation (batched)', { conversationId });

    // Clear processor state (no emission yet)
    this.chatMessageProcessor.clearMessages();
    this.conversationManager.clearAllConversations();

    // Register new conversation
    const userEmail = this.chatCommunicationService.getCurrentUserIdentifier();
    this.conversationManager.createConversation(conversationId, userEmail);

    // 🔥 FIX (FIN-0065): Use ConversationManager as single source of truth
    this.conversationManager.setActiveConversationId(conversationId);

    // Get connection status
    const connectionStatus = this.connectionManager.getConnectionStatus();

    if (connectionStatus.state !== ConnectionState.CONNECTED) {
      // Not connected - clear MessageStore and add loading message
      const tracer = trace.getTracer(this.telemetryService.getServiceName());
      const span = tracer.startSpan('chat_initialization');
      const spanContext = span.spanContext();
      const tracingContext: TracingContext = {
        traceId: spanContext.traceId,
        spanId: spanContext.spanId,
        traceFlags: spanContext.traceFlags,
        isRemote: false
      };

      const loadingMessage = this.createMessage('', true, 'CONNECTING', undefined, tracingContext, conversationId, MessageType.CONVERSATION);

      // 🔥 PHASE 3: Write to MessageStore (single source of truth)
      // First clear, then add loading message
      // MessageStore subscription will update this.messages and emit to UI
      this.messageStore.clearMessages(conversationId);
      this.messageStore.upsertMessage(loadingMessage);
      span.end();

      this.logger.info('ChatService', 'Waiting for connection before initializing conversation');
      return;
    }

    // ✅ Already connected - clear MessageStore and let bot greeting arrive via WebSocket
    // The greeting will arrive within milliseconds, so no need for intermediate loading state
    // 🔥 PHASE 3: Clear from MessageStore (single source of truth)
    // MessageStore subscription will update this.messages and emit to UI
    this.messageStore.clearMessages(conversationId);

    // Initialize conversation with backend (bot greeting will arrive via WebSocket)
    this.chatCommunicationService.initializeConversation(conversationId)
      .then(() => {
        this.logger.info('ChatService', 'New conversation initialized successfully');
      })
      .catch(error => {
        this.logger.error('ChatService', 'Failed to initialize new conversation', error);
      });
  }

  /**
   * Initialize a chat conversation
   * This sends an initial message to the server to start a conversation
   * Now leverages connection manager to ensure connection is available
   * @param conversationId The conversation ID to use for this chat session
   */
  initializeChatConversation(conversationId: string): void {
    console.log('[FRONTEND-CHAT] Initializing chat conversation with ID:', conversationId);

    // Prevent multiple initializations
    if (this.isInitializing) {
      this.logger.info('ChatService', 'Chat initialization already in progress');
      return;
    }

    this.isInitializing = true;
    this.logger.info('ChatService', 'Initializing chat conversation', { conversationId });

    // Register conversation with the conversation manager immediately
    // Get the current user identifier to ensure proper conversation tracking
    const userEmail = this.chatCommunicationService.getCurrentUserIdentifier();
    this.conversationManager.createConversation(conversationId, userEmail);

    // 🔥 FIX (FIN-0065): Use ConversationManager as single source of truth
    this.conversationManager.setActiveConversationId(conversationId);

    const initTimerId = `chat-init-${Date.now()}`;
    this.logger.startTimer(initTimerId);
    
    // Set up timeout for initialization
    const timeoutMs = 15000; // 15 second timeout
    let timeoutId: any = null;
    let loadingMessage: ChatMessage | null = null;

    // Get connection status to determine if we need to show loading state
    const connectionStatus = this.connectionManager.getConnectionStatus();

    // Check connection state before proceeding
    if (connectionStatus.state !== ConnectionState.CONNECTED) {
      // ⚠️ NOT connected - show loading state and defer initialization
      this.logger.info('ChatService', 'Connection not established, waiting for connection');

      // Add loading message with CONNECTING operation type
      const botMessages = this.messages.filter(m => m.bot);
      const hasEmptyBotMessage = botMessages.some(m =>
        !m.content.getValue() || m.content.getValue().trim() === '');

      if (!hasEmptyBotMessage) {
        // Start a span for initialization
        const tracer = trace.getTracer(this.telemetryService.getServiceName());
        const span = tracer.startSpan('chat_initialization');
        const spanContext = span.spanContext();
        const tracingContext: TracingContext = {
          traceId: spanContext.traceId,
          spanId: spanContext.spanId,
          traceFlags: spanContext.traceFlags,
          isRemote: false
        };

        loadingMessage = this.createMessage('', true, 'CONNECTING', undefined, tracingContext, conversationId, MessageType.CONVERSATION);
        this.addMessage(loadingMessage);
        span.end();
      }

      // Connection will be established by the component, which will call
      // initializeChatConversation again after connection is established
      this.isInitializing = false;
      this.logger.endTimer(initTimerId, 'ChatService', 'Chat initialization deferred until connection established');
      return;
    }

    // ✅ Already CONNECTED - skip loading state, go straight to initialization
    // No need to show "Connecting..." when we're already connected
    this.logger.info('ChatService', 'Already connected, initializing conversation immediately');

    // Set a timeout for the entire initialization
    timeoutId = setTimeout(() => {
      if (this.isInitializing) {
        this.handleInitializationTimeout();
      }
    }, timeoutMs);

    // Connection is available, initialize conversation
    this.chatCommunicationService.initializeConversation(conversationId)
      .then(() => {
        clearTimeout(timeoutId);
        this.logger.info('ChatService', 'Chat conversation initialized successfully');
      })
      .catch(error => {
        clearTimeout(timeoutId);
        this.handleInitializationError(error);
      })
      .finally(() => {
        this.logger.endTimer(initTimerId, 'ChatService', 'Chat initialized');
        this.isInitializing = false;
      });
  }

  /**
   * Handle initialization timeout
   * 🔥 PHASE 3: Uses MessageStore but keeps synchronous error display
   */
  private handleInitializationTimeout(): void {
    this.logger.error('ChatService', 'Chat initialization timeout after 15 seconds');

    // ✅ PHASE 3: Synchronously filter empty loading messages for immediate error display
    // This prevents race conditions where error message might not show before MessageStore updates
    // MessageStore subscription will eventually sync, but we need immediate feedback
    this.messages = this.messages.filter(msg =>
      !(msg.bot && (!msg.content.getValue() || msg.content.getValue().trim() === ''))
    );

    // Add error message - this writes to MessageStore
    // Get current conversationId from ConversationManager (convert null to undefined)
    const currentConversationId = this.conversationManager.getActiveConversationId() ?? undefined;
    const errorMessage = this.createMessage(
      'Connection timeout. Please refresh the page to try again.',
      true,
      'ERROR',
      undefined,
      undefined,
      currentConversationId
    );
    this.addMessage(errorMessage);

    // ✅ PHASE 3: Emit immediately for synchronous error display
    // MessageStore subscription will also emit, but this ensures immediate user feedback
    this.messagesSubject.next(this.messages);

    // Track the timeout error
    this.telemetryService.trackEvent('chat_initialization_timeout', {
      timeoutMs: 15000
    });
  }
  
  /**
   * Handle message timeout after 30 seconds
   */
  private handleMessageTimeout(messageId: string, botMessage: ChatMessage): void {
    this.logger.error('ChatService', `Message timeout after 30 seconds for messageId: ${messageId}`);
    
    // Clear the timeout from our tracking
    this.messageTimeouts.delete(messageId);
    
    // Check if the bot message still has no content
    const currentContent = botMessage.content.getValue();
    if (!currentContent || currentContent.trim() === '') {
      // Update the bot message with timeout error
      botMessage.content.next('Sorry, the request timed out. Please try again.');
      botMessage.operationType = 'ERROR';
      
      // Update the messages subject
      this.messagesSubject.next(this.messages);
      
      // Track the timeout error
      this.telemetryService.trackEvent('chat_message_timeout', {
        messageId: messageId,
        timeoutMs: 60000
      });
    }
  }

  /**
   * Handle initialization error
   * 🔥 PHASE 3: Uses MessageStore but keeps synchronous error display
   */
  private handleInitializationError(error: any): void {
    this.logger.error('ChatService', 'Failed to initialize chat conversation', error);

    // ✅ PHASE 3: Synchronously filter empty loading messages for immediate error display
    // This prevents race conditions where error message might not show before MessageStore updates
    // MessageStore subscription will eventually sync, but we need immediate feedback
    this.messages = this.messages.filter(msg =>
      !(msg.bot && (!msg.content.getValue() || msg.content.getValue().trim() === ''))
    );

    // Add error message - this writes to MessageStore
    // Get current conversationId from ConversationManager (convert null to undefined)
    const currentConversationId = this.conversationManager.getActiveConversationId() ?? undefined;
    const errorMessage = this.createMessage(
      'Failed to connect to chat service. Please try again.',
      true,
      'ERROR',
      undefined,
      undefined,
      currentConversationId
    );
    this.addMessage(errorMessage);

    // ✅ PHASE 3: Emit immediately for synchronous error display
    // MessageStore subscription will also emit, but this ensures immediate user feedback
    this.messagesSubject.next(this.messages);

    // Track the error
    this.telemetryService.trackError('chat_initialization_error', {
      error: String(error)
    });
  }

  /**
   * Add a bot message linked to a specific operation type
   * This is used by the progress integration to show loading state
   * @param operationType The operation type for progress tracking
   */
  public addBotMessageWithOperationType(operationType: string): void {
    this.logger.info('ChatService', `Adding bot message for operation: ${operationType}`);

    // Create an empty bot message with the operation type
    // Get current conversationId from ConversationManager (convert null to undefined)
    const currentConversationId = this.conversationManager.getActiveConversationId() ?? undefined;
    const message = this.createMessage('', true, operationType, undefined, undefined, currentConversationId);

    // Add to messages collection
    this.addMessage(message);

    // Track event
    this.telemetryService.trackEvent('chat_bot_operation_started', {
      operationType: operationType,
      traceId: message.tracingContext?.traceId,
      spanId: message.tracingContext?.spanId
    }, message.tracingContext?.traceId, message.tracingContext?.spanId);
  }

  /**
   * 🔥 REMOVED (PRD-001 Phase 2): deduplicateMessages() method
   * This logic is now in MessageStore.deduplicateMessages() (private method)
   * and used within MessageStore.mergeMessages() atomic transaction.
   * Moved to MessageStore to ensure deduplication happens within transaction boundary.
   */

  /**
   * Load messages from backend response during conversation sync
   * Converts backend message format to frontend ChatMessage format
   *
   * 🔥 FIX (FIN-0068 / PRD-001 Phase 1): MERGE instead of REPLACE
   * - IndexedDB is source of truth (offline-first principle)
   * - Backend messages MERGE with local instead of replacing
   * - Prevents data loss during sync (Tab 2 blank screen bug)
   * - See: .agent/tasks/PRD-001-cqrs-message-sync-architecture.md
   *
   * @param backendMessages Array of messages from backend GET /api/chat/conversation/{id}
   * @param conversationId The conversation ID these messages belong to
   */
  /**
   * 📡 SOLUTION 3: Load messages from backend ONLY if IndexedDB is empty
   * This treats backend as "cold backup", not sync partner
   *
   * Backend is pull-only:
   * - IndexedDB is always ahead of backend via real-time streaming
   * - Backend only used when IndexedDB is empty (new device/cleared data)
   * - No merge conflicts, no race conditions, simpler mental model
   *
   * @param backendMessages Messages from backend API
   * @param conversationId Conversation ID
   */
  public async loadMessagesFromBackend(backendMessages: any[], conversationId: string): Promise<void> {
    // 🔥 FIX (FIN-0065): Use ConversationManager as single source of truth
    // Set active conversation BEFORE loading to ensure messages are accepted
    this.conversationManager.setActiveConversationId(conversationId);

    try {
      // 🔥 FIX (FIN-0069): Wait for MessageStore to be fully initialized
      await this.messageStore.ready();

      // 📡 SOLUTION 3 + FIN-0101 FIX: Smart sync based on message counts
      const localMessages = await this.messageStore.getMessages(conversationId);
      const localCount = localMessages.length;
      const backendCount = backendMessages.length;

      this.logger.info('ChatService', 'Sync check', { localCount, backendCount });

      if (localCount === 0) {
        // Case 1: IndexedDB is empty - full restore from backend
        this.logger.info('ChatService', `Loading ${backendCount} messages from backend (IndexedDB empty - cold backup restore)`);
      } else if (localCount >= backendCount) {
        // Case 2: Local has same or more messages - IndexedDB is source of truth
        this.logger.info('ChatService', 'Local messages exist and up-to-date - skipping backend load');
        return;
      } else {
        // Case 3: Backend has MORE messages than local (FIN-0101 fix)
        // This happens when bot response was received but browser crashed before IndexedDB save
        this.logger.info('ChatService', `🔥 FIN-0101: Backend has ${backendCount - localCount} more messages than local - syncing missing messages`);
      }

      // Load/sync messages from backend

      for (const backendMsg of backendMessages) {
        const message = this.createMessage(
          backendMsg.content || '',
          backendMsg.bot || false,
          backendMsg.operationType,
          backendMsg.messageId,
          backendMsg.tracingContext,
          conversationId,
          backendMsg.messageType
        );

        if (backendMsg.timestamp) {
          message.timestamp = backendMsg.timestamp;
        }

        // upsertMessage() will:
        // - Write to IndexedDB
        // - Broadcast to other tabs (Solution 2)
        // - Update in-memory array
        await this.messageStore.upsertMessage(message);
      }

      this.logger.info('ChatService', '✅ Backend messages loaded to IndexedDB (cold backup restore complete)');
    } catch (error: any) {
      this.logger.error('ChatService', 'Failed to load from backend', error);
    }
  }

  /**
   * Update the last bot message with content
   * Used when an operation completes to replace loading state with actual content
   * @param content The content to set for the message
   */
  public updateLastBotMessage(content: string): void {
    if (!content || this.messages.length === 0) return;

    // Find the most recent bot message (search from end since oldest-first ordering)
    for (let i = this.messages.length - 1; i >= 0; i--) {
      if (this.messages[i].bot) {
        const previousContent = this.messages[i].content.getValue();

        // Only update if content is different
        if (content !== previousContent) {
          // Update the content subject
          this.messages[i].content.next(content);

          // Set the generation end time for response time tracking
          const endTime = Date.now();
          this.messages[i].generationEndTime = endTime;

          // Calculate response time if we have both start and end times
          const startTime = this.messages[i].generationStartTime;
          if (startTime !== undefined) {
            this.messages[i].responseTimeMs = endTime - startTime;
          }

          // ✅ REMOVED: processor.updateMessages() - processor now subscribes to MessageStore directly
          // No manual synchronization needed - reactive updates happen automatically
        }

        break;
      }
    }
  }

  /**
   * Add a temporary bot message with loading state and update it with content
   * This is a convenience method for simple operations
   * @param initialMessage Optional initial message to show while loading
   * @param operationType The type of operation for progress tracking
   * @returns A Promise that resolves when the message is added
   */
  public async addBotLoadingMessage(operationType: string, initialMessage?: string): Promise<void> {
    // First add a bot message with loading state
    this.addBotMessageWithOperationType(operationType);

    // If initial message provided, update the content after a short delay
    if (initialMessage) {
      await new Promise(resolve => setTimeout(resolve, 500));
      this.updateLastBotMessage(initialMessage);
    }
  }
  
  /**
   * Clear timeouts for messages that have received content
   */
  private clearTimeoutsForCompletedMessages(): void {
    this.messages.forEach(msg => {
      if (msg.bot && msg.messageId) {
        const content = msg.content.getValue();
        // If bot message has content and we have a timeout for it, clear it
        if (content && content.trim() !== '' && this.messageTimeouts.has(msg.messageId)) {
          const timeoutId = this.messageTimeouts.get(msg.messageId);
          clearTimeout(timeoutId);
          this.messageTimeouts.delete(msg.messageId);
          this.logger.info('ChatService', `Cleared timeout for message ${msg.messageId} - response received`);
        }
      }
    });
  }

  // 🔥 PHASE 3 Step 3: Removed ~300 lines of redundant IndexedDB code
  // All persistence is now handled by MessageStoreService:
  // - initializeIndexedDB() - MessageStore.init() handles this
  // - setupVisibilityListener() - MessageStore auto-saves on every update
  // - saveConversationToIndexedDB() - MessageStore.upsertMessage() auto-saves
  // - restoreConversationFromIndexedDB() - MessageStore.init() handles restoration
  // - clearIndexedDBConversation() - MessageStore.clearMessages() handles this
  // - showRecoveryNotification() - No longer needed (deprecated)

  /**
   * Get all messages for backup/restore operations
   */
  getAllMessages(): ChatMessage[] {
    return this.messages;
  }

  /**
   * Restore messages from backup
   * 🔥 PHASE 3 Step 2: Now writes to MessageStore instead of local array
   */
  restoreMessages(messages: any[]): void {
    // Convert plain messages from backend to proper ChatMessage objects with BehaviorSubject
    const restoredMessages = messages.map((msg: any) => {
      // Determine the correct pictureUrl based on whether it's a bot or user message
      const pictureUrl = msg.bot
        ? 'assets/svg/bot_icon.svg'
        : this.authService.getUserProfilePictureUrl();

      return {
        ...msg,
        pictureUrl: pictureUrl, // Add the pictureUrl that's missing from backend
        content: msg.content instanceof BehaviorSubject
          ? msg.content
          : new BehaviorSubject<string>(msg.content || '')
      };
    });

    // Sort messages by timestamp to ensure correct chronological order
    // 🔥 FIX: Use robust timestamp parsing that handles both:
    // - ISO strings from frontend: "2025-11-25T00:07:34.000000000Z"
    // - Numeric timestamps from backend: 1764030378872 or "1764030378872"
    restoredMessages.sort((a, b) => {
      const timeA = this.parseTimestamp(a.timestamp);
      const timeB = this.parseTimestamp(b.timestamp);
      return timeA - timeB; // Oldest first
    });

    // 🔥 PHASE 3: Write to MessageStore (single source of truth)
    // MessageStore subscription will update this.messages, messagesSubject, and processor
    this.messageStore.upsertMessages(restoredMessages);

    // ✅ PHASE 3: Processor update is now handled by MessageStore.messages$ subscription
    // No need to update here - subscription will sync processor with MessageStore
  }
}